import { useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { useWizard } from '../../../components/wizard/useWizard';
import { PreWorkInterviewStepType } from './PreWorkInterviewStepType';
import { PreWorkQuestionId } from '../../../shared/Constants';
import { PreworkResponseData } from '../../../shared/VolunteerApplicationServiceTypes';
import { useTranslation } from 'react-i18next';

export const usePreWorkInterviewFormHandling = (
	categoryValue: PreWorkInterviewStepType,
	setCategoryValue: (value: PreWorkInterviewStepType) => void,
	preWorkData: Partial<PreworkResponseData>
) => {
	const {
		setSubmitButtonDisabled,
		setCancelButtonDisabled,
		setSubmitButtonText,
	} = useWizard();
	const [form] = Form.useForm<PreWorkInterviewStepType>();
	const values = Form.useWatch<PreWorkInterviewStepType>([], form);
	const { setFieldValue } = form;
	const { t } = useTranslation();

	useEffect(() => {
		if (preWorkData.mentorcompletionStatus === 'COMPLETED') {
			setSubmitButtonText(t('ok'));
		}
	}, [preWorkData.mentorcompletionStatus, setSubmitButtonText, t]);

	const setSystemGeneratedStateOfPreWork = useCallback(() => {
		if (!values) {
			return;
		}
		const selectedOptions = Object.values(values)
			.slice(0, 3)
			.map(
				(item: {
					selectedOption: number | undefined;
					comments: string | undefined;
				}) => {
					if (item?.selectedOption) {
						return item.selectedOption;
					}
					return 0;
				}
			);

		const mentorConcernsQuestionId = PreWorkQuestionId.MENTOR_CONCERNS_QUESTION;
		const systemGeneratedQuestionId = PreWorkQuestionId.MENTOR_SYSTEM_GENERATED;

		const completedAnswersCount = selectedOptions.filter(
			(option) => option === 1
		).length;
		const incompleteAnswersCount = selectedOptions.filter(
			(option) => ![1, 2].includes(option)
		).length;

		const isFullyCompleted =
			(selectedOptions.every((option) => option === 1) ||
				completedAnswersCount === 3) &&
			values[mentorConcernsQuestionId]?.selectedOption !== 1;
		const isPartiallyCompleted =
			completedAnswersCount >= 0 &&
			incompleteAnswersCount === 0 &&
			values[mentorConcernsQuestionId]?.selectedOption !== 1;

		let systemGeneratedValue: number;

		if (isFullyCompleted) {
			systemGeneratedValue = 1;
		} else if (isPartiallyCompleted) {
			systemGeneratedValue = 2;
		} else {
			systemGeneratedValue = 3;
		}

		setFieldValue(systemGeneratedQuestionId, {
			selectedOption: systemGeneratedValue,
		});
	}, [setFieldValue, values]);

	const handleFormValuesChange = useCallback(() => {
		setSystemGeneratedStateOfPreWork();

		// This setTimeout is written to address the issue
		// When the first option is selected in the concerns question, mandatory comments field is displayed
		// On filling the comments 'Submit' button is enabled, now when the 2nd option is selected 'Submit' button is disabled even though all the form is filled.
		setTimeout(() => {
			void (async () => {
				try {
					const fieldValues = await form.validateFields({ validateOnly: true });
					setCategoryValue(fieldValues);
					setSubmitButtonDisabled(
						preWorkData.mentorcompletionStatus === 'COMPLETED'
							? false
							: !fieldValues.iConfirmAllTheAboveInfoIsTrue
					);
				} catch (e) {
					const fields = e as { values: PreWorkInterviewStepType };
					setCategoryValue(fields.values);
					setSubmitButtonDisabled(true);
				}

				setCancelButtonDisabled(false);
			})();
		}, 0);
	}, [
		form,
		preWorkData.mentorcompletionStatus,
		setCancelButtonDisabled,
		setCategoryValue,
		setSubmitButtonDisabled,
		setSystemGeneratedStateOfPreWork,
	]);

	const updateFieldValue = useCallback(
		(questionId: number, value: number) => {
			form.setFieldsValue({
				[questionId]: { selectedOption: value },
			});
		},
		[form]
	);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	useEffect(() => {
		form.setFieldsValue(categoryValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, categoryValue]);

	return { form, updateFieldValue };
};
